import { LIMITS_PARAMS_FORMATTER_FUNC } from '@perxis-auto/credit-sdk/dist';
import type { BookingRequestElements } from '@perxis-auto/forms/lib/components/BookingRequest/BookingRequest.types';
import { IconName } from '@perxis-auto/ui-kit/lib/components/Icon/Icon.types';
import type { IconSet } from '@perxis-auto/ui-kit/lib/components/Provider';

import type { IChoiceFilters } from './choice.interfaces';
import type { IConstructor } from './constructor.interfaces';
import type { IItem } from './grpc.interfaces';

export enum RouterType {
  Memory = 'memory',
  Browser = 'browser',
  Hash = 'hash',
}

export enum FiltersPosition {
  Left = 'left',
  Right = 'right',
}

export enum MapsProvider {
  Google = 'google',
  Yandex = 'yandex',
}

export enum LocationMarker {
  Dealer = 'dealer',
  Dealership = 'dealership',
  Country = 'country',
  City = 'city',
}

export enum Language {
  Ru = 'ru',
  En = 'en',
  Idn = 'idn',
}

export enum Currency {
  USD = 'USD',
  RUB = 'RUB',
  IDR = 'IDR',
}

export enum MediaType {
  Images = 'images',
  Media = 'media',
}

export enum DisclaimerDisplayType {
  Text = 'text',
  Paragraph = 'paragraph',
}

export type EVENTS_HANDLER_FUNCTION = (
  eventType: string,
  eventData: Record<string, unknown>,
) => void;

export enum DataType {
  Car = 'car',
  Filters = 'filters',
  DealerCenter = 'dealerCenter',
  City = 'city',
  SpecificationsValue = 'specificationsValue',
  SpecificationsGroup = 'SpecificationsGroup',
}

export type FormatterFunction<Item extends IItem = IItem> = (
  type: DataType,
  item: Item,
) => Item;

export interface IInitConfig {
  // Rendering options
  element: string;
  constructor?: IConstructor;

  // App navigation
  navigation: {
    type: RouterType;
    basename?: string;
    initialEntries?: string[];
  };

  // Map
  maps: {
    provider: MapsProvider;
    key: string;
    placemarkImage?: string;
    placemarkActiveImage?: string;
    placemarkHoverImage?: string;
  };

  // Feature with credit
  credit?: {
    enable?: boolean;
    apiRoot?: string;
    limitsParamsFormatter?: LIMITS_PARAMS_FORMATTER_FUNC;
  };

  // Feature with benefits
  benefits?: {
    enable?: boolean;
    showAmount?: boolean;
    showOnlyBlock?: boolean;
    disclaimerDisplayType?: DisclaimerDisplayType;
    title?: string;
    retailPrice?: string;
    totalBenefit?: string;
    priceWithBenefit?: string;
    maxBenefit?: string;
    tooltipTitle?: string;
    markersTooltipTitle?: string;
    tooltipBenefitColumn?: string;
    tooltipDiscountColumn?: string;
    tooltipPriceColumn?: string;
  };

  // Feature with models
  models?: {
    enable?: boolean;
    filterKey?: string;
    useAggregatePrice?: boolean;
  };

  // Tags
  tags?: {
    enable?: boolean;
  };

  // Perxis GRPC
  apiRoot: string;
  apiKey: string;
  spaceId: string;
  envId: string;

  // Filters
  filters: {
    enable?: boolean;
    position?: FiltersPosition;
    initialValues?: IChoiceFilters;
  };

  // Sort
  sort: {
    enable?: boolean;
    items: {
      key: string | string[];
      label: string;
      icon: IconName;
    }[];
    initialValues: string[];
  };

  // Immutable Filter Values
  countries?: string | string[];
  cities?: string | string[];
  dealers?: string | string[];
  dealerships?: string | string[];

  // Internationalization
  language?: Language;
  currency?: Currency;

  // Booking Request Form
  booking: {
    enable?: boolean;
    apiRoot: string;
    spaceId?: string;
    envId?: string;
    collectionId?: string;
  } & Partial<BookingRequestElements>;

  // Other
  locationMarker: LocationMarker | Array<LocationMarker>;
  media: {
    type: MediaType;
    apiRoot: string;
  };
  iconSet?: IconSet;

  formatter?: FormatterFunction;
}

export interface IEnvConfig {}

export interface ISettings extends IInitConfig, IEnvConfig {}
